/**
 * Microsoft Clarity loader, DSGVO-gated.
 *
 * Loads only after the user grants analytics consent. Survives reloads via
 * the same consent storage that PostHog uses (see consent.ts).
 *
 * Clarity is loaded as a side channel to PostHog session replay: it captures
 * sessions PostHog might miss (very short sessions, mobile adblockers that
 * block posthog-recorder.js but not Clarity).
 */

import { hasAnalyticsConsent, onConsentChange } from './consent';

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

const SCRIPT_ID = 'ms-clarity-loader';

function load(projectId: string): void {
  if (typeof window === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) return;

  window.clarity =
    window.clarity ||
    function (...args: unknown[]) {
      (window.clarity!.q = window.clarity!.q || []).push(args);
    };

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${projectId}`;
  document.head.appendChild(script);
}

function unload(): void {
  if (typeof window === 'undefined') return;
  const script = document.getElementById(SCRIPT_ID);
  if (script) script.remove();
  // Clarity has no first-party uninstall; cookie removal happens via banner revoke
  delete window.clarity;
}

export function initClarity(): void {
  const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
  if (!projectId || typeof window === 'undefined') return;

  // Dev: only load when explicitly enabled
  if (
    process.env.NODE_ENV === 'development' &&
    !process.env.NEXT_PUBLIC_CLARITY_DEBUG
  ) {
    return;
  }

  if (hasAnalyticsConsent()) load(projectId);

  onConsentChange((state) => {
    if (state?.analytics) load(projectId);
    else unload();
  });
}
